import { shuffleArray } from '../../../../lib/utilityFunctions';
import { getCurriculum, calculateLevelCount } from '../../content/curriculum/curriculum';
import {
  GAME_ID,
  QUESTIONS_PER_ROUND,
  DEFINITIONS_PER_BONUS_ROUND,
  GAME_PHASES,
  PUNCHLINE_CLICK_FEEDBACK_STATES,
  PUNCHLINE_BOX_DISPLAY_STATES,
  DEFINITION_BOX_DISPLAY_STATES,
  ONBOARDING_PHASES,
  SELECT_LEVEL,
  SELECT_PUNCHLINE,
  SELECT_WORD,
  SELECT_NEXT,
  SELECT_DEFINITION,
  SELECT_BACK,
  SET_ONBOARDING_PHASE,
  FINISH_ROUND,
  INIT_ACTION_SEQUENCE,
  INIT_ONBOARDING,
  SET_ALEX_STATE,
  SET_GAME_PHASE,
  INCREMENT_PROGRESS_BAR,
  ACTION_SEQUENCES,
  UNLOCK_INTERACTION,
  ACTIVATE_NEXT_BUTTON,
  HIGHLIGHT_DEFINITIONS,
  SET_HIGHLIGHTED_WORD,
  FINISH_SELECT_DEFINITION,
  INITIALIZE_JOKES,
  START_BONUS_GAME,
  PROGRESS_INCREMENT,
  FLIP_CONTENT_SIDE,
  JOKES_PER_LEVEL,
} from '../../data/punchlineConstants';

import ALEX_STATES from '../../../components/GameHeader/AlexStates';

export const initialState = () => ({
  numberOfLevels: 0,
  alexState: ALEX_STATES.INACTIVE,
  currentLevel: 0,
  // what phase of the round are we in
  currentPhase: GAME_PHASES.SELECT_PUNCHLINE,
  currentOnboardPhase: ONBOARDING_PHASES.COMPLETE,
  definitionGameIndex: 0,
  // has this user completed onboarding?
  isOnboardingComplete: false,
  // what, if any, task sequence should be played on refresh?
  sequenceToPlay: '',
  progressBarAmount: 0,
  hasHeardFirstInstruction: false,
  // top display text area
  displayText: 'How did Thomas Edison invent the light bulb?',
  punchlineClickFeedbackState: PUNCHLINE_CLICK_FEEDBACK_STATES.NONE,
  correctPunchlineIndex: 0,
  punchlineBoxesData: [
    {
      text: 'He had a bright idea.',
      style: PUNCHLINE_BOX_DISPLAY_STATES.DEFAULT,
      isCorrect: true,
    },
    {
      text: 'He worked very hard.',
      style: PUNCHLINE_BOX_DISPLAY_STATES.DEFAULT,
      isCorrect: false,
    },
  ],
  // definitions screen
  definitionData: [
    {
      text: 'a lot of light, like a light bulb',
      isCorrect: true,
      style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
    },
    {
      text: 'very smart or intelligent',
      isCorrect: true,
      style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
    },
    {
      text: 'definition3',
      isCorrect: true,
      style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
    },
  ],
  nextButtonActive: false,
  correctDefinitionsClicked: 0,
  endCurrentRound: false,
  endBonusRound: false,
  // task sequence to play on next load!
  actionSequenceToRun: ACTION_SEQUENCES.ONBOARD_FULL,
  currentTask: {},
  allowUserInput: true,
  wordToHighlight: '',
  // Which instance of the wordToHighlight should be highlighted? (In case word repeats)
  wordToHighlightOccurrence: 1,
  // we need this for reporting
  questionsAnsweredThisRound: 0,
  onboardingJoke: {
    question: 'How did Thomas Edison invent the light bulb?',
    level: -1,
    punchline: 'He had a bright idea.',
    distractor: 'He worked very hard.',
    word: 'bright',
    definitions: ['a lot of light, like a light bulb', 'very smart or intelligent'],
    definitionDistractor: 'user should never see this',
  },
  definitionReportData: {},
  jokeIndex: 0,
  contentSide: 'A',
  jokesByLevel: [],
});

export const getPunchlineDisplay = (currentJoke) => {
  const punchlineDisplay = [
    { text: '', isCorrect: false, style: PUNCHLINE_BOX_DISPLAY_STATES.DEFAULT },
    { text: '', isCorrect: false, style: PUNCHLINE_BOX_DISPLAY_STATES.DEFAULT },
  ];

  let correctIndex = 1;
  let incorrectIndex = 0;
  if (Math.round(Math.random()) === 0) {
    correctIndex = 0;
    incorrectIndex = 1;
  }

  punchlineDisplay[correctIndex].text = currentJoke.punchline;
  punchlineDisplay[correctIndex].isCorrect = true;
  punchlineDisplay[incorrectIndex].text = currentJoke.distractor;
  punchlineDisplay[incorrectIndex].isCorrect = false;

  return [punchlineDisplay, correctIndex];
};

export const getDefinitionDisplay = currentJoke => [
  {
    text: currentJoke.definitions[0],
    isCorrect: true,
    style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
  },
  {
    text: currentJoke.definitions[1],
    isCorrect: true,
    style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
  },
  {
    text: currentJoke.definitionDistractor,
    isCorrect: false,
    style: DEFINITION_BOX_DISPLAY_STATES.DEFAULT,
  },
];

const punchlineCurriculumReducer = (state = initialState(), action) => {
  if (action.gameId !== GAME_ID) {
    return state;
  }

  switch (action.type) {
    case INITIALIZE_JOKES: {
      const curriculum = getCurriculum();
      const numberOfLevels = calculateLevelCount();

      const [{ standardItems }] = curriculum.itemSets;

      // we will store our standard items in an array of three arrays, one for each level
      const jokesByLevel = Array(numberOfLevels).fill(null).map(() => ({
        A: [],
        B: [],
      }));
      // loop through all standard items in the curriculum and add it to the right level joke set
      for (let i = 0; i < standardItems.length; i += 1) {
        const { level } = standardItems[i];
        // push this standard item to the level's content set, A or B side
        if (jokesByLevel[level].A.length < 3) {
          jokesByLevel[level].A.push(standardItems[i]);
        } else {
          jokesByLevel[level].B.push(standardItems[i]);
        }
      }

      return {
        ...state,
        jokesByLevel,
        currentJoke: jokesByLevel[0].A[0], // set the first joke to be the first in the A side
        numberOfLevels,
      };
    }
    case SET_GAME_PHASE: {
      let { displayText } = state;
      if (action.payload === GAME_PHASES.VIEW_DEFINITIONS) {
        if (state.currentOnboardPhase !== ONBOARDING_PHASES.COMPLETE) {
          displayText = initialState().onboardingJoke.word;
        } else {
          displayText = state.jokesByLevel[state.currentLevel][state.contentSide][state.jokeIndex].word;
        }
      }
      return {
        ...state,
        punchlineClickFeedbackState: PUNCHLINE_CLICK_FEEDBACK_STATES.NONE,
        currentPhase: action.payload,
        displayText,
        nextButtonActive: false,
      };
    }
    // ROUND START
    case SELECT_LEVEL: {
      // onboarding?
      if (!state.isOnboardingComplete || action.payload === -1) {
        const display = getPunchlineDisplay(state.onboardingJoke);
        return {
          ...state,
          currentJoke: state.onboardingJoke,
          jokeIndex: 0,
          currentPhase: GAME_PHASES.SELECT_PUNCHLINE,
          currentOnboardPhase: ONBOARDING_PHASES.NONE,
          progressBarAmount: 0,
          displayText: initialState().displayText,
          punchlineBoxesData: display[0],
          definitionData: initialState().definitionData,
          actionSequenceToRun: ACTION_SEQUENCES.ONBOARD_FULL,
          currentLevel: action.payload,
          correctPunchlineIndex: display[1],
          questionsAnsweredThisRound: 0,
          endCurrentRound: false,
          endBonusRound: false,
        };
      }
      // set the current joke to the first in the set for this level.
      const [currentJoke] = state.jokesByLevel[action.payload][state.contentSide];
      // set display of punchline view based on current joke.
      const punchlineDisplay = getPunchlineDisplay(currentJoke);

      return {
        ...state,
        currentOnboardPhase: ONBOARDING_PHASES.COMPLETE,
        progressBarAmount: 0,
        currentLevel: action.payload,
        displayText: currentJoke.question,
        punchlineBoxesData: punchlineDisplay[0],
        correctPunchlineIndex: punchlineDisplay[1],
        currentPhase: GAME_PHASES.SELECT_PUNCHLINE,
        hasSelectedPunchline: false,
        wordToHighlight: '',
        wordToHighlightOccurrence: 1,
        actionSequenceToRun: ACTION_SEQUENCES.ONBOARD_SHORT,
        currentJoke,
        jokeIndex: 0,
        questionsAnsweredThisRound: 0,
        endCurrentRound: false,
        endBonusRound: false,
        // persist content side if the state.currentLevel is -1 (repeat onboarding) or if the level has not changed,
        // otherwise, reset to A side content.
        contentSide: state.currentLevel === -1 || state.currentLevel === action.payload ? state.contentSide : 'A',
      };
    }
    case SELECT_BACK:
      return {
        ...state,
        currentTask: {},
        punchlineClickFeedbackState: PUNCHLINE_CLICK_FEEDBACK_STATES.NONE,
        progressBarAmount: 0,
        wordToHighlight: '',
        wordToHighlightOccurrence: 1,
      };
    case SET_ONBOARDING_PHASE:
      return {
        ...state,
        currentOnboardPhase: action.payload,
      };

    case SELECT_PUNCHLINE: {
      const { punchlineBoxesData } = { ...state };
      let punchlineClickFeedbackState;
      let actionSequenceToRun;
      const isCorrect = action.payload === state.punchlineBoxesData[state.correctPunchlineIndex].text;
      const incorrectIndex = (state.correctPunchlineIndex + 1) % 2;
      if (isCorrect) {
        punchlineBoxesData[state.correctPunchlineIndex].style = PUNCHLINE_BOX_DISPLAY_STATES.CORRECT;
        punchlineBoxesData[incorrectIndex].style = PUNCHLINE_BOX_DISPLAY_STATES.FADED;
        punchlineClickFeedbackState = PUNCHLINE_CLICK_FEEDBACK_STATES.CORRECT;
        actionSequenceToRun = ACTION_SEQUENCES.PUNCHLINE_CORRECT;
      } else {
        punchlineBoxesData[state.correctPunchlineIndex].style = PUNCHLINE_BOX_DISPLAY_STATES.FADED;
        punchlineBoxesData[incorrectIndex].style = PUNCHLINE_BOX_DISPLAY_STATES.INCORRECT;
        punchlineClickFeedbackState = PUNCHLINE_CLICK_FEEDBACK_STATES.INCORRECT;
        actionSequenceToRun = ACTION_SEQUENCES.PUNCHLINE_INCORRECT;
      }

      return {
        ...state,
        punchlineBoxesData,
        punchlineClickFeedbackState,
        actionSequenceToRun,
        questionsAnsweredThisRound: state.questionsAnsweredThisRound + 1,
      };
    }
    case SELECT_WORD: {
      const { homonymWord, wordOccurrenceCount } = action.payload;
      const isCorrect = homonymWord === state.currentJoke.word;
      return {
        ...state,
        definitionData: getDefinitionDisplay(state.currentJoke),
        questionsAnsweredThisRound: state.questionsAnsweredThisRound + 1,
        actionSequenceToRun: isCorrect ? ACTION_SEQUENCES.WORD_CORRECT : ACTION_SEQUENCES.WORD_INCORRECT,
        nextButtonActive: false,
        wordToHighlight: homonymWord,
        wordToHighlightOccurrence: wordOccurrenceCount,
        hasHeardFirstInstruction: true,
      };
    }
    case SELECT_NEXT: {
      // onboarding was selected from ? button, return to progress screen
      if (state.currentLevel === -1) {
        return {
          ...state,
          // if user has not previously completed onboarding, save now.
          isOnboardingComplete: true,
          endCurrentRound: true,
          currentOnboardPhase: ONBOARDING_PHASES.COMPLETE,
          wordToHighlight: '',
          wordToHighlightOccurrence: 1,
        };
      }
      if (!state.isOnboardingComplete) {
        // setup the first joke of the level selected
        const [currentJoke] = state.jokesByLevel[state.currentLevel][state.contentSide];
        const punchlineDisplay = getPunchlineDisplay(currentJoke);
        return {
          ...state,
          currentJoke,
          jokeIndex: 0,
          currentOnboardPhase: ONBOARDING_PHASES.COMPLETE,
          currentLevel: state.currentLevel,
          displayText: currentJoke.question,
          punchlineBoxesData: punchlineDisplay[0],
          correctPunchlineIndex: punchlineDisplay[1],
          currentPhase: GAME_PHASES.SELECT_PUNCHLINE,
          hasSelectedPunchline: false,
          wordToHighlight: '',
          wordToHighlightOccurrence: 1,
          isOnboardingComplete: true,
          progressBarAmount: 0,
          questionsAnsweredThisRound: 0,
          endCurrentRound: false,
          endBonusRound: false,
        };
      }

      const nextJokeIndex = state.jokeIndex + 1;
      if (nextJokeIndex >= JOKES_PER_LEVEL) {
        return {
          ...state,
          endCurrentRound: true,
        };
      }
      // otherwise, next joke
      const currentJoke = state.jokesByLevel[state.currentLevel][state.contentSide][nextJokeIndex];
      const punchlineDisplay = getPunchlineDisplay(currentJoke);
      return {
        ...state,
        displayText: currentJoke.question,
        punchlineBoxesData: punchlineDisplay[0],
        correctPunchlineIndex: punchlineDisplay[1],
        currentPhase: GAME_PHASES.SELECT_PUNCHLINE,
        hasSelectedPunchline: false,
        currentJoke,
        jokeIndex: nextJokeIndex,
      }; }

    case START_BONUS_GAME: {
      // set current joke to first of the joke set for this level
      const [currentJoke] = state.jokesByLevel[state.currentLevel][state.contentSide];
      return {
        ...state,
        currentPhase: GAME_PHASES.SELECT_DEFINITIONS,
        displayText: currentJoke.word,
        definitionData: shuffleArray(getDefinitionDisplay(currentJoke)),
        currentJoke,
        jokeIndex: 0,
        definitionGameIndex: 0,
        correctDefinitionsClicked: 0,
        actionSequenceToRun: ACTION_SEQUENCES.SELECT_DEFINITION_INTRO,
      };
    }

    case SELECT_DEFINITION: {
      const definitionReportData = {
        action: [action.payload],
        targetAction: [],
        possibleActions: [],
        targetActionPosition: [],
      };
      const definitionData = [...state.definitionData];
      let { correctDefinitionsClicked } = state;
      let clickedCorrect = false;
      for (let i = 0; i < initialState().definitionData.length; i++) {
        definitionReportData.possibleActions.push(definitionData[i].text);
        if (definitionData[i].isCorrect) {
          definitionReportData.targetAction.push(definitionData[i].text);
          definitionReportData.targetActionPosition.push(i);
        }
        // which one did we click on
        if (definitionData[i].text === action.payload) {
          // is it correct
          if (definitionData[i].isCorrect) {
            // if we already clicked this one, bail with no change to state
            if (definitionData[i].style === DEFINITION_BOX_DISPLAY_STATES.CORRECT) {
              return state;
            }
            definitionData[i].style = DEFINITION_BOX_DISPLAY_STATES.CORRECT;
            correctDefinitionsClicked += 1;
            clickedCorrect = true;
          } else {
            // if we already clicked this one, bail with no change to state
            if (definitionData[i].style === DEFINITION_BOX_DISPLAY_STATES.INCORRECT) {
              return state;
            }
            definitionData[i].style = DEFINITION_BOX_DISPLAY_STATES.INCORRECT;
          }
        }
      }

      // if we haven't clicked two correct definitions yet...
      if (correctDefinitionsClicked < 2) {
        return {
          ...state,
          correctDefinitionsClicked,
          definitionData,
          actionSequenceToRun: clickedCorrect ?
            ACTION_SEQUENCES.PLAY_POSITIVE_SOUND : ACTION_SEQUENCES.PLAY_NEGATIVE_SOUND,
          definitionReportData,
        };
      }
      // otherwise, either load next definition OR end round
      return {
        ...state,
        correctDefinitionsClicked: 0,
        definitionData,
        actionSequenceToRun: ACTION_SEQUENCES.SECOND_CORRECT_DEFINITION,
        definitionReportData,
        questionsAnsweredThisRound: state.questionsAnsweredThisRound + 1,
        endBonusRound: state.questionsAnsweredThisRound === (QUESTIONS_PER_ROUND + DEFINITIONS_PER_BONUS_ROUND) - 1,
      };
    }
    case FINISH_ROUND:
      return {
        ...state,
        endCurrentRound: false,
      };
    case FLIP_CONTENT_SIDE:
      return {
        ...state,
        contentSide: state.contentSide === 'A' ? 'B' : 'A',
      };
    case INIT_ACTION_SEQUENCE:
      return {
        ...state,
        allowUserInput: false,
        currentTask: action.payload,
        actionSequenceToRun: '',
      };
    case INIT_ONBOARDING:
      return {
        ...state,
        allowUserInput: false,
        currentOnboardPhase: ONBOARDING_PHASES.NONE,
        alexState: ALEX_STATES.SPEAKING,
      };
    case SET_ALEX_STATE:
      return {
        ...state,
        alexState: action.payload,
      };
    case UNLOCK_INTERACTION:
      return {
        ...state,
        allowUserInput: true,
      };
    case INCREMENT_PROGRESS_BAR:
      return {
        ...state,
        progressBarAmount: state.progressBarAmount + PROGRESS_INCREMENT,
      };
    case ACTIVATE_NEXT_BUTTON:
      return {
        ...state,
        nextButtonActive: true,
      };
    case HIGHLIGHT_DEFINITIONS: {
      const currentJoke = state.jokesByLevel[state.currentLevel][state.contentSide][state.jokeIndex];
      let definitionDisplay = initialState().definitionData;
      if (state.currentOnboardPhase === ONBOARDING_PHASES.COMPLETE) {
        definitionDisplay = getDefinitionDisplay(currentJoke);
      }
      switch (action.payload) {
        case 1:
          definitionDisplay[0].style = DEFINITION_BOX_DISPLAY_STATES.CORRECT;
          definitionDisplay[1].style = DEFINITION_BOX_DISPLAY_STATES.DEFAULT;
          break;
        case 2:
          definitionDisplay[0].style = DEFINITION_BOX_DISPLAY_STATES.DEFAULT;
          definitionDisplay[1].style = DEFINITION_BOX_DISPLAY_STATES.CORRECT;
          break;
        default:
          break;
      }
      return {
        ...state,
        definitionData: definitionDisplay,
      };
    }
    case SET_HIGHLIGHTED_WORD:
      return {
        ...state,
        wordToHighlight:
          state.currentOnboardPhase !== ONBOARDING_PHASES.COMPLETE
            ? initialState().onboardingJoke.word
            : state.jokesByLevel[state.currentLevel][state.contentSide][state.jokeIndex].word,
        wordToHighlightOccurrence: 1,
      };
    case FINISH_SELECT_DEFINITION: {
      // if defGameIndex > 2, end round
      if (state.definitionGameIndex === 2) {
        return {
          ...state,
          endBonusRound: true,
        };
      }
      // otherwise, return next definition
      const currentJoke = state.jokesByLevel[state.currentLevel][state.contentSide][state.definitionGameIndex + 1];
      return {
        ...state,
        correctDefinitionsClicked: 0,
        displayText: currentJoke.word,
        definitionData: shuffleArray(getDefinitionDisplay(currentJoke)),
        definitionGameIndex: state.definitionGameIndex + 1,
        currentJoke,
      };
    }
    default:
      return state;
  }
};

export default punchlineCurriculumReducer;
